// Ombor va zaxira — `xsib-inventory` `stock` moduli bilan kontrakt.
//
//   GET    /api/inventory/stock/items?activeOnly&page&size&search&sortField&sortOrder
//                                                    → {rows, total, page, size}
//   POST   /api/inventory/stock/items                → StockItem (201)
//   PUT    /api/inventory/stock/items/{id}           → StockItem
//   GET    /api/inventory/stock/warehouses           → Warehouse[]
//
//   POST   /api/inventory/stock/receipts             → StockOperation
//   POST   /api/inventory/stock/issues               → StockOperation
//   GET    /api/inventory/stock/transfers            → ListPage<StockTransfer>
//   POST   /api/inventory/stock/transfers            → StockTransfer (jo'natildi, yo'lda)
//   POST   /api/inventory/stock/transfers/{id}/receive → StockTransfer (qabul qilindi)
//   POST   /api/inventory/stock/returns/{id}/accept  → StockTransfer (qaytarish qabul qilindi)
//   POST   /api/inventory/stock/returns/{id}/reject  → StockTransfer (qaytarish rad etildi)
//   POST   /api/inventory/stock/adjustments          → StockOperation
//   POST   /api/inventory/stock/write-offs           → StockOperation
//
//   GET    /api/inventory/stock/balances?nodeId&itemId&belowMinOnly    → StockBalanceRow[]
//   GET    /api/inventory/stock/movements?nodeId&itemId&kind&from&to&page&size
//                                                    → {rows, total, page, size}
//   GET    /api/inventory/stock/valuation?nodeId     → StockValuation
//
//   GET    /api/inventory/stock/policy               → StockPolicy
//   PUT    /api/inventory/stock/policy               → StockPolicy
//   GET    /api/inventory/stock/approvals?nodeId&pendingOnly → StockApproval[]
//   POST   /api/inventory/stock/approvals/{id}/approve → StockOperation
//   POST   /api/inventory/stock/approvals/{id}/reject   → StockApproval
//   POST   /api/inventory/stock/approvals/{id}/cancel   → StockApproval
//
// Huquqlar — UCHTA va ular ataylab uchta (`StockResource` javadoc'i):
//   `inventory.stock.read`   — qoldiq, jurnal, qiymat, ma'lumotnoma,
//                              chegara va tasdiq so'rovlari RO'YXATI
//   `inventory.stock.write`  — kirim, chiqim, ko'chirish, mol turi
//   `inventory.stock.adjust` — inventarizatsiya va hisobdan chiqarish,
//                              hamda tasdiq/rad/bekor QARORI.
//                              ⚠️ XAVFLI: qoldiqni KONTRAGENTSIZ
//                              o'zgartiradi ("20 metr kabel yo'qoldi" —
//                              faqat yozgan odamning so'zi).
//
// ⚠️ TO'RTINCHI HUQUQ — BOSHQA MODULDAN: `PUT /policy` `settings.update`
// talab qiladi, `inventory.stock.adjust` EMAS. Bu ONGLI qaror
// (`StockResource#updatePolicy` javadoc'i): aks holda nazorat qilinayotgan
// omborchi chegarani 0 ga tushirib, hisobdan chiqarib, keyin qaytarib
// qo'ya olardi — uch amal, hammasi o'z huquqi bilan.
//
// =====================================================================
// ⚠️ 1-QOIDA — JURNAL O'ZGARMAS
// =====================================================================
// `stock_movement` yozuvini bazadagi `stock_movement_no_update` triggeri
// himoya qiladi (`ledger.entry` bilan bir xil qaror). Shuning uchun bu
// faylda `updateMovement`/`deleteMovement` YO'Q va panelda harakat
// qatorida tahrirlash tugmasi CHIZILMAYDI. Xato bo'lsa — TESKARI
// harakat yoziladi.
//
// =====================================================================
// ⚠️ 2-QOIDA — OMBOR = TUGUN, VA U IMKONIYAT BILAN ANIQLANADI (§19.3)
// =====================================================================
// "Omborlar jadvali" yo'q. Ombor — `platform.org_node` daraxtidagi,
// turida `holds_stock = true` bo'lgan tugun (`Warehouse` javadoc'i).
// Ro'yxatni server beradi va u ALLAQACHON ikki marta filtrlangan:
// imkoniyat bo'yicha VA ko'rinish doirasi bo'yicha. Panel `"WAREHOUSE"`
// satrini hech qayerda yozmaydi.
//
// =====================================================================
// ⚠️ 3-QOIDA — PUL MINOR BIRLIKDA, BUTUN SON (§19.1)
// =====================================================================
// `unitCostMinor` — MINOR birlik (`Money.of(long, currency)`).
// Valyuta YUBORILMAYDI: bitta tenantda bitta valyuta va uni bazadagi
// `stock_movement_currency_fill` triggeri to'ldiradi. Panelda
// aylantirish FAQAT `MoneyInput` ichida bo'ladi — u yagona joy.
//
// =====================================================================
// ⚠️ 4-QOIDA — TAKRORIY SO'ROV (§20.9-D)
// =====================================================================
// "Ombor harakati takrorlansa — qoldiq noto'g'ri". Har yozish amali
// `idempotencyKey` bilan ketadi va u AMAL BOSHLANGANDA bir marta
// yasaladi (`newIdempotencyKey`), qayta urinishda O'ZGARMAYDI. Server
// takroriy so'rovda yangi harakat yozmaydi va `replayed: true` qaytaradi
// — panel buni YASHIRMAYDI.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { moneySchema, instantSchema, toMoney, uuidSchema } from '@/shared/api/schemas'
import {
  fetchAllRows,
  listParams,
  serverPageSchema,
  toPageResult,
  type SortFieldMap,
} from '@/shared/api/serverPage'
import type { Money } from '@/shared/lib/money'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

/**
 * Harakat turi — `MovementKind` enum'ining nusxasi.
 *
 * ⚠️ Ro'yxat backend bilan AYNAN mos bo'lishi shart: bazada
 * `stock_movement_sign_matches_kind` cheklovi shu ro'yxatga tayanadi.
 * Panel yangi tur o'ylab topolmaydi.
 */
export const MOVEMENT_KINDS = [
  'RECEIPT',
  'OPENING',
  'ISSUE',
  'SALE',
  'SALE_RETURN',
  'INTERNAL_USE',
  'TRANSFER_OUT',
  'TRANSFER_IN',
  'ADJUSTMENT',
  'DAMAGED',
  'LOST',
] as const
export type MovementKind = (typeof MOVEMENT_KINDS)[number]

/**
 * Hisobdan chiqarish sababi — `StockCommand.WriteOffKind`.
 *
 * ⚠️ `MovementKind` ning kichik nusxasi EMAS, API chegarasi:
 * `/write-offs` ga `RECEIPT` yuborib bo'lmasligi TIPDA ko'rinadi.
 */
export const WRITE_OFF_KINDS = ['DAMAGED', 'LOST'] as const
export type WriteOffKind = (typeof WRITE_OFF_KINDS)[number]

/** `StockItemDraft` dagi `@Pattern` ning nusxasi */
export const SKU_PATTERN = /^[A-Za-z0-9._-]{2,64}$/

/** `MovementQuery.MAX_SIZE` — kattaroq so'ralsa server JIMGINA shuni qo'yadi */
export const MOVEMENT_MAX_PAGE_SIZE = 200

// --- Mol turi ----------------------------------------------------------------

const stockItemSchema = z.object({
  id: uuidSchema,
  /** ⚠️ Javobda bor, lekin panel ishlatmaydi: izolyatsiya chegarasi, ma'lumot emas */
  tenantId: uuidSchema.nullish(),
  sku: z.string(),
  name: z.string(),
  unit: z.string(),
  minQuantity: z.number(),
  active: z.boolean(),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

export interface StockItem {
  id: string
  sku: string
  name: string
  /** Erkin matn ("dona", "metr"). ⚠️ Kasr miqdor QO'LLAB-QUVVATLANMAYDI */
  unit: string
  /** ⚠️ Ogohlantirish chegarasi, TAQIQ EMAS. `0` = ogohlantirish yo'q */
  minQuantity: number
  active: boolean
  createdAt: string | null
  updatedAt: string | null
}

function toStockItem(raw: z.infer<typeof stockItemSchema>): StockItem {
  return {
    id: raw.id,
    sku: raw.sku,
    name: raw.name,
    unit: raw.unit,
    minQuantity: raw.minQuantity,
    active: raw.active,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
  }
}

/** URL filtri kaliti — jadval va sarlavhadagi tanlagich bir xil nom ishlatadi */
export const STOCK_ITEM_ACTIVE_FILTER = 'active'

/**
 * «Nofaollari bilan» tanlovining URL'dagi qiymati.
 *
 * ⚠️ Sukut — YO'QLIK, ya'ni faqat faollar (egasi qarori 2026-08-20,
 * «yopilganlar qoidasi»). Shuning uchun bayroqni AYNAN shu qiymat
 * o'chiradi, boshqa hech narsa emas.
 */
export const STOCK_ITEM_WITH_INACTIVE = 'all'

/**
 * Ro'yxat SO'ROVIDAGI `activeOnly` — jadval holatidan hisoblanadi.
 *
 * ⚠️ ALOHIDA VA EKSPORT QILINGAN, `fetchStockItemPage` ichida yashirilgan
 * emas: `subscriberListParams` bilan aynan bir sabab bo'yicha
 * (`tests/closed-filter.mjs` sarlavhasi) — manba matnida naqsh qidirish
 * xulqni ko'rmasdi, bu funksiya esa qorovulda CHAQIRILADI.
 *
 * ⚠️ Solishtirish `!== STOCK_ITEM_WITH_INACTIVE`, `=== 'active'` EMAS:
 * eski havoladan yoki qo'lda tahrirlangan URL'dan kelgan notanish qiymat
 * nofaollarni JIMGINA ochib yubormasin — sukut har doim yopiq tomonga
 * og'adi.
 */
export function stockItemActiveOnly(state: TableState): boolean {
  return state.filters[STOCK_ITEM_ACTIVE_FILTER]?.[0] !== STOCK_ITEM_WITH_INACTIVE
}

/**
 * Ustun kaliti → `GET /api/inventory/stock/items` ning `sortField` enumi.
 *
 * ⚠️ ESKI SUKUT TARTIB (`sku`) YO'Q — endi `CREATED_AT DESC`. SKU bo'yicha
 * tartib kerak bo'lsa «Nomi» ustuni bo'yicha saralanadi (u SKU ni ham
 * ko'rsatadi va `SKU` enumiga bog'langan).
 */
const STOCK_ITEM_SORT_FIELDS: SortFieldMap = {
  sku: 'SKU',
  name: 'NAME',
  minQuantity: 'MIN_QUANTITY',
  createdAt: 'CREATED_AT',
}

async function loadStockItemPage(
  params: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<{ rows: StockItem[]; total: number }> {
  const response = await api.get<unknown>('/api/inventory/stock/items', {
    params,
    ...(signal ? { signal } : {}),
  })
  const parsed = serverPageSchema(stockItemSchema).parse(response.data)
  return { rows: parsed.rows.map(toStockItem), total: parsed.total }
}

/**
 * Bir SAHIFA mol turi — qidiruv, saralash, `activeOnly` va sahifalash
 * SERVERDA.
 */
export async function fetchStockItemPage(
  state: TableState,
  signal: AbortSignal,
): Promise<PageResult<StockItem>> {
  const page = await loadStockItemPage(
    {
      ...listParams(state, { sortFields: STOCK_ITEM_SORT_FIELDS }),
      activeOnly: stockItemActiveOnly(state),
    },
    signal,
  )
  return toPageResult(page, page.rows)
}

/**
 * BUTUN katalog — mol TANLAGICHLARI uchun (kirim, chiqim, qoldiq,
 * tasdiq ekranlari), jadval uchun EMAS.
 */
export async function fetchStockItems(activeOnly: boolean): Promise<readonly StockItem[]> {
  return fetchAllRows((page, size) => loadStockItemPage({ page, size, activeOnly }))
}

export interface StockItemDraft {
  sku: string
  name: string
  unit: string
  minQuantity: number
  /**
   * ⚠️ HAR DOIM yuboriladi, hatto tahrirda o'zgarmagan bo'lsa ham.
   * Backendda u `Boolean` (primitiv emas) va sabab `QARORLAR.md` §21.7
   * da o'lchangan: tushib qolgan maydonni Jackson `false` qilib
   * to'ldirardi va qisman `PUT` molni JIMGINA nofaol qilib qo'yardi.
   */
  active: boolean
}

export async function createStockItem(draft: StockItemDraft): Promise<StockItem> {
  const response = await api.post<unknown>('/api/inventory/stock/items', draft)
  return toStockItem(stockItemSchema.parse(response.data))
}

export async function updateStockItem(id: string, draft: StockItemDraft): Promise<StockItem> {
  const response = await api.put<unknown>(`/api/inventory/stock/items/${id}`, draft)
  return toStockItem(stockItemSchema.parse(response.data))
}

// --- Ombor (tugun) -----------------------------------------------------------

const warehouseSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  displayName: z.string(),
  /** ⚠️ Turning KODI — FAQAT ko'rsatish uchun. Unga qarab qaror qabul qilinmaydi */
  kind: z.string(),
  state: z.string(),
  /** Turning imkoniyati — ombor mexanizmi FAQAT shunga qaraydi */
  holdsStock: z.boolean(),
})

export interface Warehouse {
  id: string
  code: string
  displayName: string
  kind: string
  state: string
  holdsStock: boolean
}

/** Bosh ombor (ADR-043): ko'chirish faqat u bilan filial ombori o'rtasida bo'ladi. */
export const MAIN_WAREHOUSE_KIND = 'MAIN_WAREHOUSE'

export function isMainWarehouse(warehouses: readonly Warehouse[], nodeId: string | null | undefined): boolean {
  return warehouses.some((warehouse) => warehouse.id === nodeId && warehouse.kind === MAIN_WAREHOUSE_KIND)
}

/**
 * Chaqiruvchi YOZA OLADIGAN omborlar.
 *
 * ⚠️ Bu daraxt ro'yxatining nusxasi EMAS: server uni imkoniyat
 * (`holds_stock`) VA ko'rinish doirasi bo'yicha filtrlab beradi. Panel
 * ombor tanlash maydonini shundan to'ldiradi — aks holda operator o'zi
 * yoza olmaydigan omborni tanlab, 404 olardi.
 */
export async function fetchWarehouses(): Promise<readonly Warehouse[]> {
  const response = await api.get<unknown>('/api/inventory/stock/warehouses')
  return z.array(warehouseSchema).parse(response.data)
}

/**
 * Shu ombor mol QABUL QILA oladimi — kirim yoki ko'chirish MANZILI
 * bo'la oladimi.
 *
 * ⚠️ NEGA FAQAT QABUL TOMONIDA. Yopilgan omborga yangi qoldiq
 * qo'shish uni o'lik tugunda hosil qilardi va uni keyin faqat yana
 * bitta harakat bilan chiqarish mumkin bo'lardi. CHIQUVCHI tomonda esa
 * yopilgan ombor QOLADI: unda qoldiq qolib ketgan bo'lishi mumkin va
 * uni ko'chirib olish kerak. Ro'yxatdan butunlay olib tashlash o'sha
 * qoldiqni panel orqali umuman qimirlatib bo'lmaydigan qilardi.
 *
 * ⚠️ Backend bu shartni TEKSHIRMAYDI (`StockServiceImpl` faqat
 * `holds_stock` va ko'rinish doirasiga qaraydi), ya'ni bu — panelning
 * o'z qorovuli va u yolg'iz turadi.
 *
 * ⚠️ `SUSPENDED` QOLADI: u vaqtinchalik holat, `CLOSED` esa terminal
 * (`employeeNodes` dagi bilan bir xil qaror).
 */
export function canReceiveStock(warehouse: Warehouse): boolean {
  return warehouse.state !== 'CLOSED'
}

// --- Qoldiq ------------------------------------------------------------------

const balanceRowSchema = z.object({
  nodeId: uuidSchema,
  nodeName: z.string().nullish(),
  itemId: uuidSchema,
  sku: z.string(),
  name: z.string(),
  unit: z.string(),
  quantity: z.number(),
  minQuantity: z.number(),
  /** ⚠️ SERVERDA hisoblanadi, saqlanmaydi — panel uni QAYTA hisoblamaydi */
  belowMin: z.boolean(),
})

export interface StockBalance {
  nodeId: string
  nodeName: string | null
  itemId: string
  sku: string
  name: string
  unit: string
  quantity: number
  minQuantity: number
  belowMin: boolean
}

/**
 * Qoldiqlar.
 *
 * ⚠️ `belowMinOnly` — minimal qoldiq ogohlantirishining TORTIB olinadigan
 * yarmi. Itarib yuboriladigani allaqachon bor va u faqat CHIQIM QILGAN
 * odamga tegadi (`StockOperation.lowStock`); bu ro'yxat esa o'sha paytda
 * yonida turmagan ombor mudiri uchun.
 *
 * ⚠️ Filtr SERVERNIKI, panel qatorlarni o'zi kesmaydi: shart bazada
 * `belowMin` bayrog'i bilan AYNAN bir xil (`min_quantity > 0 AND quantity
 * < min_quantity`). Panelda ikkinchi ta'rif yozilsa, ular bir kun ajralib
 * ketardi.
 *
 * ⚠️ Qatori BO'LMAGAN mol bu ro'yxatga TUSHMAYDI (server izohi): har
 * ombor har molni saqlamaydi. Ekranda bu ochiq aytiladi, aks holda
 * "hammasi joyida" degan noto'g'ri xulosa chiqardi.
 */
export async function fetchStockBalances(
  nodeId: string | null,
  itemId: string | null,
  belowMinOnly: boolean,
  signal?: AbortSignal,
): Promise<readonly StockBalance[]> {
  const response = await api.get<unknown>('/api/inventory/stock/balances', {
    ...(signal ? { signal } : {}),
    params: {
      ...(nodeId ? { nodeId } : {}),
      ...(itemId ? { itemId } : {}),
      ...(belowMinOnly ? { belowMinOnly: true } : {}),
    },
  })
  return z
    .array(balanceRowSchema)
    .parse(response.data)
    .map((raw) => ({ ...raw, nodeName: raw.nodeName ?? null }))
}

// --- Harakatlar jurnali ------------------------------------------------------

const movementSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema.nullish(),
  itemId: uuidSchema,
  nodeId: uuidSchema,
  /** Ko'chirishning ikkinchi tarafi; faqat `TRANSFER_*` da */
  counterNodeId: uuidSchema.nullish(),
  transferGroup: uuidSchema.nullish(),
  kind: z.enum(MOVEMENT_KINDS),
  /** ⚠️ ISHORALI: musbat = kirim, manfiy = chiqim */
  quantity: z.number(),
  /** JAMI tannarx (birlik emas), ishorasi `quantity` bilan bir xil */
  cost: moneySchema,
  docNo: z.string().nullish(),
  reason: z.string().nullish(),
  /** `INTERNAL_USE` — asosiy vositaning inventar yoki seriya raqami */
  assetTag: z.string().nullish(),
  idempotencyKey: z.string().nullish(),
  actorId: z.string().nullish(),
  occurredAt: instantSchema,
  createdAt: instantSchema.nullish(),
})

export interface StockMovement {
  id: string
  itemId: string
  nodeId: string
  counterNodeId: string | null
  transferGroup: string | null
  kind: MovementKind
  quantity: number
  cost: Money
  docNo: string | null
  reason: string | null
  assetTag: string | null
  actorId: string | null
  /** Hodisaning HAQIQIY vaqti (orqaga sana bilan ham) */
  occurredAt: string
  /** Jurnalga yozilgan vaqt — hech qachon o'zgarmaydi */
  createdAt: string | null
}

function toMovement(raw: z.infer<typeof movementSchema>): StockMovement {
  return {
    id: raw.id,
    itemId: raw.itemId,
    nodeId: raw.nodeId,
    counterNodeId: raw.counterNodeId ?? null,
    transferGroup: raw.transferGroup ?? null,
    kind: raw.kind,
    quantity: raw.quantity,
    cost: toMoney(raw.cost),
    docNo: raw.docNo ?? null,
    reason: raw.reason ?? null,
    assetTag: raw.assetTag ?? null,
    actorId: raw.actorId ?? null,
    occurredAt: raw.occurredAt,
    createdAt: raw.createdAt ?? null,
  }
}

// ⚠️ Simdagi maydon `rows` — loyihadagi BARCHA sahifalangan javoblar
//    bilan bir xil (`tests/api-contract.mjs` §4, `PageFieldNameGuardTest`).
//    2026-08-14 gacha backend uni `items` deb yuborardi; nom faqat
//    backend tomonda o'zgartirilsa bu sxema javobni RAD etardi va
//    jadval o'rniga mangu skeleton qolardi — shuning uchun ikkalasi
//    bitta commit'da o'zgardi.
//
// ⚠️ Pastdagi `PageResult` ning `items` i BOSHQA narsa: u panelning
//    ICHKI `DataTable` shartnomasi, simdagi shakl emas — tegilmaydi.
const movementPageSchema = z.object({
  rows: z.array(movementSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})

export interface MovementFilter {
  nodeId: string | null
  itemId: string | null
  kind: MovementKind | null
  /** ISO-8601, `occurred_at >= from` */
  from: string | null
  /** ISO-8601, `occurred_at < to` */
  to: string | null
}

/**
 * ⚠️ Jurnalda QIDIRUV maydoni YO'Q va bu ataylab: backend `movements`
 * uchun matnli qidiruvni umuman qabul qilmaydi (`MovementQuery` da `q`
 * yo'q). Qidiruv maydonini chizib, uni jimgina e'tiborsiz qoldirish
 * "qidirdim — topilmadi" degan YOLG'ON javob bo'lardi.
 */
export function fetchMovementPage(
  filter: MovementFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<StockMovement>> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/inventory/stock/movements', {
      signal,
      params: {
        ...(filter.nodeId ? { nodeId: filter.nodeId } : {}),
        ...(filter.itemId ? { itemId: filter.itemId } : {}),
        ...(filter.kind ? { kind: filter.kind } : {}),
        ...(filter.from ? { from: filter.from } : {}),
        ...(filter.to ? { to: filter.to } : {}),
        page: state.page - 1,
        size: Math.min(state.pageSize, MOVEMENT_MAX_PAGE_SIZE),
      },
    })
    const parsed = movementPageSchema.parse(response.data)
    return { items: parsed.rows.map(toMovement), total: parsed.total }
  }
}

// --- Zaxira qiymati ----------------------------------------------------------

const valuationSchema = z.object({
  nodeId: uuidSchema.nullish(),
  lines: z.array(
    z.object({
      itemId: uuidSchema,
      sku: z.string(),
      name: z.string(),
      unit: z.string(),
      quantity: z.number(),
      value: moneySchema,
    }),
  ),
  total: moneySchema,
})

export interface StockValuationLine {
  itemId: string
  sku: string
  name: string
  unit: string
  quantity: number
  value: Money
}

export interface StockValuation {
  nodeId: string | null
  lines: readonly StockValuationLine[]
  total: Money
}

/**
 * Zaxira qiymati — SO'RALGANDA hisoblanadi, saqlanmaydi.
 *
 * ⚠️ Bu MOLIYAVIY HISOBOT EMAS: unda "yopilgan davr" tushunchasi yo'q
 * va u `ledger` ga hech narsa yozmaydi (`StockValuation` javadoc'i,
 * `QARORLAR.md` §20.2). Panelda shu OCHIQ aytiladi — aks holda
 * foydalanuvchi uni buxgalteriya hisoboti deb qabul qiladi.
 */
export async function fetchStockValuation(
  nodeId: string | null,
  signal?: AbortSignal,
): Promise<StockValuation> {
  const response = await api.get<unknown>('/api/inventory/stock/valuation', {
    ...(signal ? { signal } : {}),
    params: { ...(nodeId ? { nodeId } : {}) },
  })
  const parsed = valuationSchema.parse(response.data)
  return {
    nodeId: parsed.nodeId ?? null,
    lines: parsed.lines.map((line) => ({ ...line, value: toMoney(line.value) })),
    total: toMoney(parsed.total),
  }
}

// --- Nazorat siyosati (chegara) ----------------------------------------------

const stockPolicySchema = z.object({
  /** ⚠️ MINOR birlikda, tashkilot valyutasida. `0` — nazorat O'CHIQ */
  threshold: moneySchema,
  /** Ichki foydalanish chegarasi; `0` — tasdiqsiz */
  internalUseThreshold: moneySchema,
  updatedAt: instantSchema.nullish(),
  updatedBy: z.string().nullish(),
})

export interface StockPolicy {
  /** ⚠️ `amount === 0` — tasdiqlash oqimi umuman ishlamaydi (sukut holat) */
  threshold: Money
  internalUseThreshold: Money
  /** Sozlanmagan bo'lsa `null` — "hech kim tegmagan" va "bugun 0 qo'yildi" boshqa holat */
  updatedAt: string | null
  updatedBy: string | null
}

function toPolicy(raw: z.infer<typeof stockPolicySchema>): StockPolicy {
  return {
    threshold: toMoney(raw.threshold),
    internalUseThreshold: toMoney(raw.internalUseThreshold),
    updatedAt: raw.updatedAt ?? null,
    updatedBy: raw.updatedBy ?? null,
  }
}

/**
 * Chegarani O'QISH — `inventory.stock.read` ostida.
 *
 * ⚠️ O'zgartirish bilan ADASHMANG: `PUT` `settings.update` talab qiladi.
 * Ikki huquq bitta ekranda uchrashadi va panel ularni ALOHIDA
 * tekshirishi shart (`StockApprovalsPage` javadoc'i).
 */
export async function fetchStockPolicy(signal?: AbortSignal): Promise<StockPolicy> {
  const response = await api.get<unknown>('/api/inventory/stock/policy', {
    ...(signal ? { signal } : {}),
  })
  return toPolicy(stockPolicySchema.parse(response.data))
}

/**
 * Chegarani o'zgartirish — `settings.update`.
 *
 * ⚠️ `thresholdMinor` MAJBURIY va u `Long`, primitiv emas: backendda
 * tushib qolgan maydonni Jackson `0` qilib to'ldirardi va so'rov
 * nazoratni JIMGINA o'chirib qo'yardi (`StockPolicyDraft` javadoc'i,
 * `QARORLAR.md` §21.7). Shuning uchun panel ham uni har doim yuboradi.
 */
export async function updateStockPolicy(
  thresholdMinor: number,
  internalUseThresholdMinor: number,
): Promise<StockPolicy> {
  const response = await api.put<unknown>('/api/inventory/stock/policy', {
    approvalThresholdMinor: thresholdMinor,
    internalUseThresholdMinor,
  })
  return toPolicy(stockPolicySchema.parse(response.data))
}

// --- Ikkinchi imzo (tasdiqlash oqimi) ----------------------------------------

/**
 * So'rovning holati — `ApprovalState` enum'ining nusxasi.
 *
 * ⚠️ TERMINAL HOLATDAN CHIQISH YO'LI YO'Q va uni bazadagi
 * `stock_approval_state_guard` triggeri majburlaydi. Panel shu sababdan
 * "qayta ochish" tugmasini CHIZMAYDI — u har doim 409 olardi.
 *
 * ⚠️ `EXPIRED` YO'Q va bo'lmaydi ham: "bir hafta javob bo'lmasa o'zi
 * o'tsin" degan nazorat — nazorat emas (`ApprovalState` javadoc'i).
 */
export const APPROVAL_STATES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const
export type ApprovalState = (typeof APPROVAL_STATES)[number]

/**
 * Tasdiq oqimiga tushadigan harakat turlari.
 *
 * ⚠️ Ro'yxat `MovementKind#reasonRequired()` bilan bir xil va bu TASODIF
 * EMAS (`ApprovalState.isApprovable` javadoc'i): sabab matni majburiy
 * bo'lgan amallar — aynan ular qoldiqni KONTRAGENTSIZ o'zgartiradi.
 * Kirim, chiqim va ko'chirish tushmaydi — ularning kontragenti bor va
 * ular ish yo'lida turadi.
 */
export const APPROVAL_KINDS = ['INTERNAL_USE', 'ADJUSTMENT', 'DAMAGED', 'LOST'] as const
export type ApprovalKind = (typeof APPROVAL_KINDS)[number]

/**
 * ⚠️ `kind` KENG ro'yxat bilan o'qiladi (`MOVEMENT_KINDS`), `APPROVAL_KINDS`
 * bilan EMAS. Sabab amaliy: `zod` notanish qiymatda BUTUN ro'yxatni rad
 * etadi, ya'ni bitta kutilmagan qator kutayotgan so'rovlarning
 * HAMMASINI ekrandan yo'qotardi. Server tomonda cheklov allaqachon bor
 * (`ApprovalState.isApprovable`), panelda uni takrorlash faqat zarar.
 */
const stockApprovalSchema = z.object({
  id: uuidSchema,
  /** ⚠️ Javobda bor, lekin panel ishlatmaydi: izolyatsiya chegarasi */
  tenantId: uuidSchema.nullish(),
  nodeId: uuidSchema,
  itemId: uuidSchema,
  kind: z.enum(MOVEMENT_KINDS),
  quantity: z.number(),
  /** So'rov PAYTIDAGI baho, musbat. ⚠️ Chegara bilan solishtirilgan AYNAN shu son */
  estimatedCost: moneySchema,
  /** O'sha paytda amal qilgan chegara — keyin o'zgarsa ham so'rovda qoladi */
  threshold: moneySchema,
  reason: z.string().nullish(),
  /** `INTERNAL_USE` — seriya yoki inventar raqami */
  assetTag: z.string().nullish(),
  docNo: z.string().nullish(),
  occurredAt: instantSchema.nullish(),
  idempotencyKey: z.string().nullish(),
  state: z.enum(APPROVAL_STATES),
  requestedBy: z.string(),
  requestedAt: instantSchema,
  decidedBy: z.string().nullish(),
  decidedAt: instantSchema.nullish(),
  decisionNote: z.string().nullish(),
  movementId: uuidSchema.nullish(),
})

export interface StockApproval {
  id: string
  nodeId: string
  itemId: string
  kind: MovementKind
  /**
   * ⚠️ MA'NOSI TURGA QARAB O'ZGARADI: `ADJUSTMENT` da — SANALGAN miqdor
   * (farq emas), `DAMAGED`/`LOST` da — chiqim miqdori. Ekranda ular
   * boshqa-boshqa yozuv bilan ko'rsatiladi, aks holda sanoq varaqasidagi
   * "42" hisobdan chiqariladigan 42 bo'lib o'qilardi.
   */
  quantity: number
  estimatedCost: Money
  threshold: Money
  reason: string | null
  assetTag: string | null
  docNo: string | null
  occurredAt: string | null
  state: ApprovalState
  /**
   * KIM so'radi.
   *
   * ⚠️ Bu USERNAME EMAS, aksariyat hollarda foydalanuvchi UUID'si —
   * o'lchandi (2026-08-13): `xsib-gateway` `X-Xsib-Username` sarlavhasini
   * UMUMAN yubormaydi, `PermissionHeaderFilter` esa u bo'lmaganda
   * `userId.toString()` ga tushadi. Shuning uchun taqqoslash `isOwnApproval`
   * ichida IKKALASI bo'yicha ham qilinadi.
   */
  requestedBy: string
  requestedAt: string
  decidedBy: string | null
  decidedAt: string | null
  decisionNote: string | null
  /**
   * ⚠️ `APPROVED` bo'lib turib `null` bo'lishi MUMKIN va bu xato emas:
   * sanoq farqi tasdiq kutayotganda boshqa amal bilan yopilgan bo'lsa,
   * yoziladigan hech narsa qolmaydi (`StockApproval` javadoc'i).
   */
  movementId: string | null
}

function toApproval(raw: z.infer<typeof stockApprovalSchema>): StockApproval {
  return {
    id: raw.id,
    nodeId: raw.nodeId,
    itemId: raw.itemId,
    kind: raw.kind,
    quantity: raw.quantity,
    estimatedCost: toMoney(raw.estimatedCost),
    threshold: toMoney(raw.threshold),
    reason: raw.reason ?? null,
    assetTag: raw.assetTag ?? null,
    docNo: raw.docNo ?? null,
    occurredAt: raw.occurredAt ?? null,
    state: raw.state,
    requestedBy: raw.requestedBy,
    requestedAt: raw.requestedAt,
    decidedBy: raw.decidedBy ?? null,
    decidedAt: raw.decidedAt ?? null,
    decisionNote: raw.decisionNote ?? null,
    movementId: raw.movementId ?? null,
  }
}

export async function fetchStockApprovals(
  nodeId: string | null,
  pendingOnly: boolean,
  signal?: AbortSignal,
): Promise<readonly StockApproval[]> {
  const response = await api.get<unknown>('/api/inventory/stock/approvals', {
    ...(signal ? { signal } : {}),
    params: {
      ...(nodeId ? { nodeId } : {}),
      pendingOnly,
    },
  })
  return z.array(stockApprovalSchema).parse(response.data).map(toApproval)
}

/**
 * Tasdiqlash — harakat AYNAN SHU PAYTDA yoziladi.
 *
 * ⚠️ Javob `StockOperation`, `StockApproval` EMAS: tasdiq bir vaqtning
 * o'zida jurnal qatorini ham yozadi va u `movements` da qaytadi. Qoldiq
 * shu paytda o'zgaradi, ya'ni `lowStock` ogohlantirishi ham shu javobda
 * chiqishi mumkin.
 */
export async function approveStockApproval(id: string, note: string | null): Promise<StockOperation> {
  const response = await api.post<unknown>(
    `/api/inventory/stock/approvals/${id}/approve`,
    { note },
  )
  return toOperation(operationSchema.parse(response.data))
}

export async function rejectStockApproval(id: string, note: string | null): Promise<StockApproval> {
  const response = await api.post<unknown>(`/api/inventory/stock/approvals/${id}/reject`, { note })
  return toApproval(stockApprovalSchema.parse(response.data))
}

/**
 * Bekor qilish — faqat so'rovni YOZGAN odam.
 *
 * ⚠️ Tanasiz `POST`: backend bu yerda izoh umuman olmaydi
 * (`StockResource#cancel`). Panelda izoh maydonini chizib uni jimgina
 * tashlab yuborish "yozdim — saqlanmadi" degan yolg'on bo'lardi.
 */
export async function cancelStockApproval(id: string): Promise<StockApproval> {
  const response = await api.post<unknown>(`/api/inventory/stock/approvals/${id}/cancel`)
  return toApproval(stockApprovalSchema.parse(response.data))
}

// --- Amallar -----------------------------------------------------------------

const lowStockSchema = z.object({
  itemId: uuidSchema,
  sku: z.string(),
  nodeId: uuidSchema,
  quantity: z.number(),
  minQuantity: z.number(),
})

const operationSchema = z.object({
  movements: z.array(movementSchema),
  replayed: z.boolean(),
  lowStock: lowStockSchema.nullish(),
  /**
   * ⚠️ SXEMADA BO'LISHI SHART. `zod` notanish maydonni JIMGINA tashlab
   * yuboradi, ya'ni usiz chegaradan oshgan hisobdan chiqarish panelda
   * "hech narsa o'zgarmadi" bo'lib ko'rinardi — `movements` bo'sh
   * bo'lgani uchun. Bu `equipmentSchema.nodeId` bilan AYNAN bir xil
   * tuzoq (`guards.mjs` 7-bo'limi).
   */
  approval: stockApprovalSchema.nullish(),
})

export interface LowStock {
  itemId: string
  sku: string
  nodeId: string
  /** Amaldan KEYINGI qoldiq */
  quantity: number
  minQuantity: number
}

export interface StockOperation {
  /** 1 yoki 2 ta qator (ko'chirishda `TRANSFER_OUT` + `TRANSFER_IN`) */
  movements: readonly StockMovement[]
  /** ⚠️ `true` — takroriy so'rov edi, YANGI hech narsa yozilmadi */
  replayed: boolean
  /** ⚠️ XATO EMAS, ogohlantirish. `null` — chegaradan pastga tushmadi */
  lowStock: LowStock | null
  /**
   * Tasdiq so'rovi; `null` — tasdiq umuman talab qilinmadi.
   *
   * ⚠️ IKKI XIL MA'NO va ular `movements` bilan farqlanadi
   * (`StockOperation` javadoc'i):
   *   · `movements` bo'sh + `PENDING` — amal BAJARILMADI, qoldiq
   *     TEGILMAGAN, ikkinchi imzo kutilmoqda;
   *   · `movements` to'la + `APPROVED` — bu tasdiqlash natijasi.
   */
  approval: StockApproval | null
}

function toOperation(raw: z.infer<typeof operationSchema>): StockOperation {
  return {
    movements: raw.movements.map(toMovement),
    replayed: raw.replayed,
    lowStock: raw.lowStock ?? null,
    approval: raw.approval ? toApproval(raw.approval) : null,
  }
}

/** ⚠️ HAR YERDA MUSBAT. Ishorani `MovementKind` beradi, operator emas */
interface CommonCommand {
  docNo: string | null
  reason: string | null
  /** ISO-8601; `null` — server "hozir" ni qo'yadi */
  occurredAt: string | null
  idempotencyKey: string
}

export interface ReceiptCommand extends CommonCommand {
  nodeId: string
  itemId: string
  quantity: number
  /** ⚠️ MINOR birlikda, butun son. Bepul kirim bo'lsa `0` */
  unitCostMinor: number
}

export interface IssueCommand extends CommonCommand {
  nodeId: string
  itemId: string
  quantity: number
}

export interface DispatchCommand {
  fromNodeId: string
  toNodeId: string
  itemId: string
  quantity: number
  /** Faqat bosh ombordan filialga — birlik ichki narxi, minor birlikda */
  unitPriceMinor: number | null
  returnReason: string | null
  returnCondition: string | null
  serialNumbers: string | null
  docNo: string | null
  note: string | null
  idempotencyKey: string
}

/**
 * ⚠️ FARQNI emas, SANALGAN MIQDORNI oladi (`StockCommand.Adjust`
 * javadoc'i): operator qo'lida sanoq varaqasi bo'ladi ("42 dona bor"),
 * farq esa hisoblanadigan narsa. Farqni so'rasak, bitta ishora xatosi
 * ikki barobar farq berardi.
 */
export interface AdjustCommand extends CommonCommand {
  nodeId: string
  itemId: string
  countedQuantity: number
  /** ⚠️ MAJBURIY */
  reason: string
}

export interface WriteOffCommand extends CommonCommand {
  nodeId: string
  itemId: string
  quantity: number
  kind: WriteOffKind
  /** ⚠️ MAJBURIY */
  reason: string
}

/** Mol o'z ehtiyojiga olinadi va asosiy vositaga o'tadi; sana — server vaqti */
export interface InternalUseCommand {
  nodeId: string
  itemId: string
  quantity: number
  /** ⚠️ MAJBURIY */
  reason: string
  /** ⚠️ MAJBURIY — seriya yoki inventar raqami */
  assetTag: string
  idempotencyKey: string
}

async function postOperation(path: string, body: unknown): Promise<StockOperation> {
  const response = await api.post<unknown>(`/api/inventory/stock/${path}`, body)
  return toOperation(operationSchema.parse(response.data))
}

export function receiveStock(command: ReceiptCommand): Promise<StockOperation> {
  return postOperation('receipts', command)
}

export function issueStock(command: IssueCommand): Promise<StockOperation> {
  return postOperation('issues', command)
}

// --- Ko'chirish (ikki bosqich) -------------------------------------------------

export const TRANSFER_STATES = ['IN_TRANSIT', 'RECEIVED', 'REJECTED'] as const
export type TransferState = (typeof TRANSFER_STATES)[number]

export const TRANSFER_DIRECTIONS = ['SUPPLY', 'RETURN', 'LEGACY'] as const
export type TransferDirection = (typeof TRANSFER_DIRECTIONS)[number]

const transferSchema = z.object({
  id: uuidSchema,
  itemId: uuidSchema,
  sku: z.string(),
  itemName: z.string(),
  unit: z.string(),
  fromNodeId: uuidSchema,
  fromNodeName: z.string(),
  toNodeId: uuidSchema,
  toNodeName: z.string(),
  quantitySent: z.number(),
  direction: z.enum(TRANSFER_DIRECTIONS),
  cost: moneySchema,
  inNodeCost: moneySchema,
  unitPrice: moneySchema.nullish(),
  state: z.enum(TRANSFER_STATES),
  docNo: z.string().nullish(),
  note: z.string().nullish(),
  dispatchedBy: z.string(),
  dispatchedAt: instantSchema,
  receivedBy: z.string().nullish(),
  receivedAt: instantSchema.nullish(),
  quantityReceived: z.number().nullish(),
  quantityDamaged: z.number().nullish(),
  receiveNote: z.string().nullish(),
  returnReason: z.string().nullish(),
  returnCondition: z.string().nullish(),
  serialNumbers: z.string().nullish(),
  rejectedBy: z.string().nullish(),
  rejectedAt: instantSchema.nullish(),
  rejectNote: z.string().nullish(),
})

export interface StockTransfer {
  id: string
  itemId: string
  sku: string
  itemName: string
  unit: string
  fromNodeId: string
  fromNodeName: string
  toNodeId: string
  toNodeName: string
  quantitySent: number
  direction: TransferDirection
  /** Kompaniya tannarxi */
  cost: Money
  /** Oluvchi markaz tannarxi: filialga o'tkazmada ichki narx bo'yicha */
  inNodeCost: Money
  unitPrice: Money | null
  state: TransferState
  docNo: string | null
  note: string | null
  dispatchedBy: string
  dispatchedAt: string
  receivedBy: string | null
  receivedAt: string | null
  quantityReceived: number | null
  quantityDamaged: number | null
  receiveNote: string | null
  returnReason: string | null
  returnCondition: string | null
  serialNumbers: string | null
  rejectedBy: string | null
  rejectedAt: string | null
  rejectNote: string | null
}

function toTransfer(raw: z.infer<typeof transferSchema>): StockTransfer {
  return {
    ...raw,
    cost: toMoney(raw.cost),
    inNodeCost: toMoney(raw.inNodeCost),
    unitPrice: raw.unitPrice ? toMoney(raw.unitPrice) : null,
    docNo: raw.docNo ?? null,
    note: raw.note ?? null,
    receivedBy: raw.receivedBy ?? null,
    receivedAt: raw.receivedAt ?? null,
    quantityReceived: raw.quantityReceived ?? null,
    quantityDamaged: raw.quantityDamaged ?? null,
    receiveNote: raw.receiveNote ?? null,
    returnReason: raw.returnReason ?? null,
    returnCondition: raw.returnCondition ?? null,
    serialNumbers: raw.serialNumbers ?? null,
    rejectedBy: raw.rejectedBy ?? null,
    rejectedAt: raw.rejectedAt ?? null,
    rejectNote: raw.rejectNote ?? null,
  }
}

export interface TransferFilter {
  state: TransferState | null
  nodeId: string | null
}

export function fetchTransferPage(
  filter: TransferFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<StockTransfer>> {
  return async (table, signal) => {
    const response = await api.get<unknown>('/api/inventory/stock/transfers', {
      signal,
      params: {
        ...(filter.state ? { state: filter.state } : {}),
        ...(filter.nodeId ? { nodeId: filter.nodeId } : {}),
        page: table.page - 1,
        size: table.pageSize,
      },
    })
    const parsed = z
      .object({ rows: z.array(transferSchema), total: z.number() })
      .parse(response.data)
    return { items: parsed.rows.map(toTransfer), total: parsed.total }
  }
}

export async function dispatchTransfer(command: DispatchCommand): Promise<StockTransfer> {
  const response = await api.post<unknown>('/api/inventory/stock/transfers', command)
  return toTransfer(transferSchema.parse(response.data))
}

export interface TransferReceipt {
  receivedQuantity: number
  damagedQuantity: number
  note: string | null
}

export async function receiveTransfer(id: string, receipt: TransferReceipt): Promise<StockTransfer> {
  const response = await api.post<unknown>(`/api/inventory/stock/transfers/${id}/receive`, receipt)
  return toTransfer(transferSchema.parse(response.data))
}

export async function acceptReturn(id: string, receipt: TransferReceipt): Promise<StockTransfer> {
  const response = await api.post<unknown>(`/api/inventory/stock/returns/${id}/accept`, receipt)
  return toTransfer(transferSchema.parse(response.data))
}

export async function rejectReturn(id: string, note: string): Promise<StockTransfer> {
  const response = await api.post<unknown>(`/api/inventory/stock/returns/${id}/reject`, { note })
  return toTransfer(transferSchema.parse(response.data))
}

/** Yetib kelmagan miqdor — jo'natilgandan qabul va shikast ayirmasi; manfiy bo'lsa forma xato */
export function missingQuantity(sent: number, received: number, damaged: number): number {
  return sent - received - damaged
}

export function adjustStock(command: AdjustCommand): Promise<StockOperation> {
  return postOperation('adjustments', command)
}

export function writeOffStock(command: WriteOffCommand): Promise<StockOperation> {
  return postOperation('write-offs', command)
}

export function internalUseStock(command: InternalUseCommand): Promise<StockOperation> {
  return postOperation('internal-use', command)
}

// --- Sof mantiq --------------------------------------------------------------

/**
 * Takroriy so'rov kaliti — §20.9-D.
 *
 * ⚠️ Amal BOSHLANGANDA bir marta yasaladi va qayta urinishda
 * O'ZGARMAYDI. Har urinishda yangisini yasash idempotentlikni butunlay
 * bekor qilardi: server ikkinchi so'rovni yangi amal deb qabul qilib,
 * qoldiqni ikki marta o'zgartirardi.
 *
 * ⚠️ `crypto.randomUUID` mavjud bo'lmagan muhitda (eski brauzer, HTTP
 * ustidagi test stendi) zaxira yo'l ishlaydi — kalitning yagonaligi
 * kriptografik emas, amaliy talab.
 */
export function newIdempotencyKey(): string {
  const cryptoApi = globalThis.crypto as Crypto | undefined
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID()
  }
  return `stock-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/**
 * Harakat qoldiqni oshiradimi.
 *
 * ⚠️ Turdan EMAS, ISHORADAN aniqlanadi. `MovementKind` → yo'nalish
 * xaritasini panelda takrorlash ikkinchi haqiqat manbai bo'lardi:
 * `ADJUSTMENT` ikki tomonlama va uni xaritada to'g'ri ifodalab
 * bo'lmasdi. Ishora esa serverdan keladi va bazadagi
 * `stock_movement_sign_matches_kind` bilan kafolatlangan.
 */
export function isInbound(movement: Pick<StockMovement, 'quantity'>): boolean {
  return movement.quantity > 0
}

/**
 * Inventarizatsiya farqi — sanoq natijasidan.
 *
 * ⚠️ Panel bu sonni FAQAT KO'RSATADI ("qoldiq 40 → sanaldi 42, farq
 * +2"), serverga esa SANALGAN miqdor ketadi. Farqni yuborish
 * `AdjustCommand` javadoc'idagi xatoni qaytarardi.
 *
 * ⚠️ Nol farq — HECH QANDAY harakat: server ham shunday qiladi (nol
 * yozuv shovqin). Panel buni oldindan aytadi, aks holda operator
 * "bosdim, jurnalda yo'q" degan holatga tushardi.
 */
export function adjustmentDelta(current: number, counted: number): number {
  return counted - current
}

/**
 * Molning shu ombordagi joriy qoldig'i — tanlangan qatorlardan.
 *
 * ⚠️ `undefined` va `0` FARQLANADI: birinchisi "bu juftlik uchun qator
 * yo'q" (mol bu omborda hech qachon bo'lmagan), ikkinchisi "bor edi,
 * tugadi". Chiqim oynasida ikkalasi ham chiqimni taqiqlaydi, lekin
 * SABABI boshqa va operatorga boshqacha tushuntiriladi.
 */
export function balanceOf(
  balances: readonly StockBalance[],
  nodeId: string | null,
  itemId: string | null,
): StockBalance | null {
  if (nodeId === null || itemId === null) return null
  return balances.find((row) => row.nodeId === nodeId && row.itemId === itemId) ?? null
}

// --- Ikkinchi imzo: sof qoidalar ---------------------------------------------

/**
 * Chaqiruvchining kimligi — SERVER YOZGAN shaklda.
 *
 * ⚠️ IKKI MAYDON, va ikkalasi ham kerak. `stock_approval.requested_by` ga
 * `SecurityContextHolder ... auth.getName()` yoziladi, u esa
 * `PermissionHeaderFilter` da shunday hosil bo'ladi:
 *
 *     X-Xsib-Username bo'lsa  → username
 *     bo'lmasa                → userId.toString()
 *
 * ⚠️ 2026-08-13 da o'lchandi: `xsib-gateway` bu sarlavhani UMUMAN
 * yubormaydi (`XsibHeaders` da u yo'q), ya'ni JONLI tizimda u UUID.
 * Faqat `username` bo'yicha taqqoslash "o'z so'rovim" ni HECH QACHON
 * topmasdi: «Bekor qilish» tugmasi ko'rinmasdi va o'zini o'zi
 * tasdiqlash ogohlantirishi hech qachon chiqmasdi — panel 403 ni
 * faqat bosgandan keyin ko'rsatardi.
 */
export interface ApprovalIdentity {
  /** `/api/me` dagi `userId` */
  id: string
  username: string
}

/**
 * So'rovni SHU odam yozganmi.
 *
 * ⚠️ Bo'sh qiymat hech qachon "meniki" emas: `requestedBy` bo'sh kelsa
 * (yoki kimligimiz noma'lum bo'lsa) taqqoslash `false` beradi va panel
 * "bekor qila olaman" deb yolg'on aytmaydi.
 */
export function isOwnApproval(
  approval: Pick<StockApproval, 'requestedBy'>,
  identity: ApprovalIdentity | null,
): boolean {
  if (identity === null) return false
  const who = approval.requestedBy.trim()
  if (who === '') return false
  return who === identity.id || who === identity.username
}

/** Panelda ko'rsatiladigan qaror amallari — backend yo'llarining nusxasi */
export const APPROVAL_ACTIONS = ['APPROVE', 'REJECT', 'CANCEL'] as const
export type ApprovalAction = (typeof APPROVAL_ACTIONS)[number]

/**
 * Amal nima uchun BAJARIB BO'LMAYDI — yoki `OK`.
 *
 * ⚠️ Bu ro'yxat backend qoidalarining NUSXASI, yangi qoida EMAS. Har
 * qiymat aniq bitta server javobiga to'g'ri keladi:
 *
 * | Natija          | Server                                    |
 * |-----------------|-------------------------------------------|
 * | `NO_PERMISSION` | `@RequiresPermission` → 403               |
 * | `DECIDED`       | `StockException.ApprovalNotPending` → 409  |
 * | `SELF`          | `StockException.SelfApproval` → 403        |
 * | `NOT_REQUESTER` | `StockException.NotRequester` → 403        |
 *
 * ⚠️ TARTIB SERVERNIKI bilan bir xil: huquq interceptor'da (eng oldin),
 * keyin `requirePending`, keyin kimligi tekshiriladi. Panel boshqa
 * tartibda yozsa, u boshqa SABABNI ko'rsatardi — masalan huquqi yo'q
 * odamga "o'z so'rovingiz" deb, ya'ni tugmani berishsa ishlaydi degan
 * taassurot qoldirardi.
 *
 * ⚠️ RAD ETISH o'z so'roviga ham OCHIQ va bu backenddagi holat:
 * `rejectApproval` da `SelfApproval` tekshiruvi YO'Q (faqat
 * `approveApproval` da bor). Ma'nosi ham bor — o'z so'rovini rad etish
 * uni bekor qilish bilan bir xil natija beradi.
 */
export type ApprovalBlock = 'OK' | 'NO_PERMISSION' | 'DECIDED' | 'SELF' | 'NOT_REQUESTER'

export function approvalBlock(
  action: ApprovalAction,
  approval: Pick<StockApproval, 'state' | 'requestedBy'>,
  identity: ApprovalIdentity | null,
  canAdjust: boolean,
): ApprovalBlock {
  // Uchala yo'l ham `inventory.stock.adjust` ostida — BEKOR QILISH ham
  // (`StockResource#cancel`). Bu ataylab: so'rovni yozgan odamda bu
  // huquq allaqachon bor, aks holda so'rov umuman paydo bo'lmasdi.
  if (!canAdjust) return 'NO_PERMISSION'
  if (approval.state !== 'PENDING') return 'DECIDED'
  const own = isOwnApproval(approval, identity)
  if (action === 'APPROVE' && own) return 'SELF'
  if (action === 'CANCEL' && !own) return 'NOT_REQUESTER'
  return 'OK'
}

/**
 * Amal BAJARILMADI, ikkinchi imzo kutilmoqda.
 *
 * ⚠️ Shart IKKI qismli: `movements` bo'sh VA so'rov `PENDING`. Faqat
 * `approval !== null` ga qarash yaramaydi — tasdiqlash javobida ham
 * `approval` bor, lekin u yerda amal ALLAQACHON yozilgan. Faqat
 * `movements.length === 0` ga qarash ham yaramaydi — nol farqli sanoq
 * ham bo'sh qaytadi va u butunlay boshqa xabar ("hech narsa
 * o'zgarmadi").
 */
export function isAwaitingApproval(
  operation: Pick<StockOperation, 'movements' | 'approval'>,
): boolean {
  return operation.movements.length === 0 && operation.approval?.state === 'PENDING'
}

/**
 * Amal natijasi qanday O'QILISHI kerak — beshta holat, beshta boshqa
 * xabar.
 *
 * ⚠️ NEGA SOF FUNKSIYA, komponent ichidagi shart emas. Bu tanlov
 * ILGARI `StockOperationAlert` ichida uch qavatli uchlik operator edi
 * va uning TARTIBI ma'noni belgilardi: `empty` sharti `awaiting` dan
 * oldin turgan kuni chegaradan oshgan hisobdan chiqarish «hech narsa
 * o'zgarmadi» bo'lib chiqardi. Bunday nuqsonni matn bo'yicha tekshirib
 * bo'lmaydi (shart o'chirilgan bo'lsa ham tartib to'g'ri ko'rinadi) —
 * sof funksiyani esa XULQI bo'yicha sinash mumkin.
 *
 * | Natija            | Qachon | Ekranda |
 * |-------------------|--------|---------|
 * | `AWAITING`        | so'rov yozildi, qoldiq tegilmagan | sariq |
 * | `AWAITING_REPLAY` | shu kalit bo'yicha so'rov ALLAQACHON kutmoqda | sariq |
 * | `REPLAYED`        | takroriy so'rov, avvalgi natija | sariq |
 * | `NO_CHANGE`       | nol farqli sanoq — jurnalga qator yozilmadi | sariq |
 * | `DONE`            | amal bajarildi | yashil |
 */
export type StockResultKind = 'AWAITING' | 'AWAITING_REPLAY' | 'REPLAYED' | 'NO_CHANGE' | 'DONE'

export function stockResultKind(
  operation: Pick<StockOperation, 'movements' | 'replayed' | 'approval'>,
): StockResultKind {
  // ⚠️ KUTISH ENG BIRINCHI: uning belgisi ham `movements` ning bo'sh
  // bo'lishi va shu sababdan u qolgan ikkala "bo'sh" holat bilan
  // adashtirilishi mumkin.
  if (isAwaitingApproval(operation)) {
    return operation.replayed ? 'AWAITING_REPLAY' : 'AWAITING'
  }
  if (operation.replayed) return 'REPLAYED'
  return operation.movements.length === 0 ? 'NO_CHANGE' : 'DONE'
}

/**
 * Chegara YOQILGANMI.
 *
 * ⚠️ `0` — o'chiq va bu TO'LIQ HUQUQIY holat, sozlanmagan ham emas:
 * tashkilot bu nazoratni xohlamasligi mumkin. Panel shu sababdan uni
 * "sozlanmagan" deb emas, "o'chiq" deb yozadi.
 */
export function approvalRequired(policy: Pick<StockPolicy, 'threshold'>): boolean {
  return policy.threshold.amount > 0
}

/**
 * `quantity` maydoni SANOQ natijasimi.
 *
 * ⚠️ `ADJUSTMENT` da u SANALGAN miqdor (buyruq shakli bilan bir xil),
 * qolgan ikkitasida esa chiqim miqdori (`StockApproval` javadoc'i).
 * Bitta yorliq bilan ko'rsatish sanoq varaqasidagi "42" ni "42 dona
 * hisobdan chiqariladi" deb o'qitardi.
 */
export function isCountedQuantity(kind: MovementKind): boolean {
  return kind === 'ADJUSTMENT'
}
